/**
 * Custom React Hook สำหรับการใช้งานฐานข้อมูล
 * ให้ความสะดวกในการเรียกใช้ API และจัดการ state
 */

import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/ApiService';

// Hook สำหรับจัดการข้อมูลผู้ใช้
export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getUsers();
      if (result.success) {
        setUsers(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.createUser(userData);
      if (result.success) {
        setUsers(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (id, userData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.updateUser(id, userData);
      if (result.success) {
        setUsers(prev => prev.map(user => user.id === id ? result.data : user));
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteUser = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.deleteUser(id);
      if (result.success) {
        setUsers(prev => prev.filter(user => user.id !== id));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser
  };
};

// Hook สำหรับจัดการข้อมูลสต๊อกแบตเตอรี่
export const useBatteryStock = (filters = {}) => {
  const [batteryStock, setBatteryStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBatteryStock = useCallback(async (searchFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getBatteryStock(searchFilters);
      if (result.success) {
        setBatteryStock(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createBatteryStock = useCallback(async (stockData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.createBatteryStock(stockData);
      if (result.success) {
        setBatteryStock(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBatteryStock = useCallback(async (id, stockData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.updateBatteryStock(id, stockData);
      if (result.success) {
        setBatteryStock(prev => prev.map(item => item.id === id ? result.data : item));
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteBatteryStock = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.deleteBatteryStock(id);
      if (result.success) {
        setBatteryStock(prev => prev.filter(item => item.id !== id));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatteryStock();
  }, []);

  return {
    batteryStock,
    loading,
    error,
    fetchBatteryStock,
    createBatteryStock,
    updateBatteryStock,
    deleteBatteryStock
  };
};

// Hook สำหรับจัดการข้อมูลสต๊อกชาร์จเจอร์
export const useChargerStock = (filters = {}) => {
  const [chargerStock, setChargerStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchChargerStock = useCallback(async (searchFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getChargerStock(searchFilters);
      if (result.success) {
        setChargerStock(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createChargerStock = useCallback(async (stockData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.createChargerStock(stockData);
      if (result.success) {
        setChargerStock(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateChargerStock = useCallback(async (id, stockData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.updateChargerStock(id, stockData);
      if (result.success) {
        setChargerStock(prev => prev.map(item => item.id === id ? result.data : item));
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteChargerStock = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.deleteChargerStock(id);
      if (result.success) {
        setChargerStock(prev => prev.filter(item => item.id !== id));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChargerStock();
  }, []);

  return {
    chargerStock,
    loading,
    error,
    fetchChargerStock,
    createChargerStock,
    updateChargerStock,
    deleteChargerStock
  };
};

// Hook สำหรับจัดการข้อมูลลูกค้า
export const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getCustomers();
      if (result.success) {
        setCustomers(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createCustomer = useCallback(async (customerData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.createCustomer(customerData);
      if (result.success) {
        setCustomers(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCustomer = useCallback(async (id, customerData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.updateCustomer(id, customerData);
      if (result.success) {
        setCustomers(prev => prev.map(customer => customer.id === id ? result.data : customer));
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCustomer = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.deleteCustomer(id);
      if (result.success) {
        setCustomers(prev => prev.filter(customer => customer.id !== id));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return {
    customers,
    loading,
    error,
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer
  };
};

// Hook สำหรับจัดการข้อมูลการขาย
export const useSales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('useSales: Fetching sales data...');
      const result = await apiService.getSales();
      console.log('useSales: API result:', result);
      if (result.success) {
        setSales(result.data);
        console.log('useSales: Sales data set:', result.data);
      } else {
        setError(result.error);
        console.error('useSales: Fetch error:', result.error);
      }
    } catch (err) {
      setError(err.message);
      console.error('useSales: Fetch exception:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createSale = useCallback(async (saleData) => {
    setLoading(true);
    setError(null);
    try {
      console.log('useSales: Creating sale with data:', saleData);
      const result = await apiService.createSale(saleData);
      console.log('useSales: Create sale result:', result);
      if (result.success) {
        setSales(prev => [result.data, ...prev]);
        console.log('useSales: Sale added to state:', result.data);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        console.error('useSales: Create sale error:', result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      console.error('useSales: Create sale exception:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSale = useCallback(async (saleData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.updateSale(saleData.id, saleData);
      if (result.success) {
        setSales(prev => prev.map(sale => sale.id === saleData.id ? result.data : sale));
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSale = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      console.log('useSales: Deleting sale ID:', id);
      const result = await apiService.deleteSale(id);
      console.log('useSales: Delete result:', result);
      
      // API call สำเร็จ (ไม่มี exception) = การลบสำเร็จ
      setSales(prev => {
        const newSales = prev.filter(sale => sale.id !== id);
        console.log('useSales: Updated sales after deletion:', newSales);
        return newSales;
      });
      return { success: true };
    } catch (err) {
      console.error('useSales: Delete error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  return {
    sales,
    loading,
    error,
    fetchSales,
    createSale,
    updateSale,
    deleteSale
  };
};

// Hook สำหรับจัดการข้อมูลใบเสนอราคา
export const useQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchQuotations = useCallback(async () => {
    console.log('=== fetchQuotations called ===');
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getQuotations();
      console.log('fetchQuotations result:', result);
      if (result.success) {
        setQuotations(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('fetchQuotations error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createQuotation = useCallback(async (quotationData) => {
    console.log('=== createQuotation hook called ===', quotationData);
    setLoading(true);
    setError(null);
    try {
      console.log('=== Calling apiService.createQuotation ===');
      const result = await apiService.createQuotation(quotationData);
      console.log('=== createQuotation API result ===', result);
      if (result.success) {
        setQuotations(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('=== createQuotation error ===', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateQuotation = useCallback(async (id, quotationData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.updateQuotation(id, quotationData);
      if (result.success) {
        setQuotations(prev => prev.map(q => q.id === parseInt(id) ? result.data : q));
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteQuotation = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.deleteQuotation(id);
      if (result.success) {
        setQuotations(prev => prev.filter(q => q.id !== parseInt(id)));
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotations();
  }, []);

  return {
    quotations,
    loading,
    error,
    fetchQuotations,
    createQuotation,
    updateQuotation,
    deleteQuotation
  };
};

// Hook สำหรับจัดการการเข้าสู่ระบบ
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.login(username, password);
      if (result.success) {
        setUser(result.data);
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const checkAuth = useCallback(() => {
    // ข้อมูลผู้ใช้จะถูกจัดการผ่าน MySQL API แทน localStorage
    // การตรวจสอบสิทธิ์จะทำผ่าน backend API
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  return {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth
  };
};

// Hook สำหรับสถิติ Dashboard
export const useDashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getDashboardStats();
      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  return {
    stats,
    loading,
    error,
    fetchStats
  };
};